import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Slider } from '@/components/ui/slider'
import { Switch } from '@/components/ui/switch'
import { Button } from '@/components/ui/button'

export interface PromptConfig {
  systemPrompt: string
  opener: string
  emptyResponse: string
  similarityThreshold: number
  keywordsSimilarityWeight: number
  topN: number
  showQuote: boolean
}

interface Step4PromptConfigProps {
  config: PromptConfig
  onConfigChange: (config: PromptConfig) => void
  errors: {
    systemPrompt?: string
  }
}

const DEFAULT_SYSTEM_PROMPT = `You are an intelligent assistant. Please summarize the content of the knowledge base to answer the question. Please list the data in the knowledge base and answer in detail. When all knowledge base content is irrelevant to the question, your answer must include the sentence "The answer you are looking for is not found in the knowledge base!" Answers need to consider chat history. Here is the knowledge base: {knowledge} The above is the knowledge base.`

export const DEFAULT_PROMPT_CONFIG: PromptConfig = {
  systemPrompt: DEFAULT_SYSTEM_PROMPT,
  opener: 'Hi! I am your assistant, can I help you?',
  emptyResponse: "I couldn't find any relevant information in my knowledge base.",
  similarityThreshold: 0.2,
  keywordsSimilarityWeight: 0.7,
  topN: 6,
  showQuote: true,
}

export function Step4PromptConfig({
  config,
  onConfigChange,
  errors,
}: Step4PromptConfigProps) {
  const handleReset = () => {
    onConfigChange(DEFAULT_PROMPT_CONFIG)
  }

  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-lg font-semibold mb-1">Prompt & Retrieval Settings</h3>
        <p className="text-sm text-muted-foreground">
          Configure how the assistant responds and retrieves information
        </p>
      </div>

      {/* System Prompt */}
      <div className="space-y-2">
        <Label htmlFor="systemPrompt">
          System Prompt <span className="text-destructive">*</span>
        </Label>
        <Textarea
          id="systemPrompt"
          value={config.systemPrompt}
          onChange={(e) => onConfigChange({ ...config, systemPrompt: e.target.value })}
          rows={12}
          className={`resize-y font-mono text-sm ${errors.systemPrompt ? 'border-destructive' : ''}`}
        />
        <div className="flex justify-between text-xs">
          {errors.systemPrompt ? (
            <span className="text-destructive">{errors.systemPrompt}</span>
          ) : (
            <span className="text-muted-foreground">
              Use {'{knowledge}'} variable to reference retrieved content (reserved variable)
            </span>
          )}
        </div>
      </div>

      {/* Opening Message */}
      <div className="space-y-2">
        <Label htmlFor="opener">Opening Message</Label>
        <Input
          id="opener"
          value={config.opener}
          onChange={(e) => onConfigChange({ ...config, opener: e.target.value })}
          placeholder="Hi! I am your assistant, can I help you?"
        />
        <p className="text-xs text-muted-foreground">
          First message shown to users
        </p>
      </div>

      {/* Empty Response */}
      <div className="space-y-2">
        <Label htmlFor="emptyResponse">Empty Response</Label>
        <Input
          id="emptyResponse"
          value={config.emptyResponse}
          onChange={(e) => onConfigChange({ ...config, emptyResponse: e.target.value })}
          placeholder="I couldn't find any relevant information..."
        />
        <p className="text-xs text-muted-foreground">
          Response when no relevant information is found
        </p>
      </div>

      {/* Similarity Threshold */}
      <div className="space-y-3">
        <div className="flex justify-between items-center">
          <Label>Similarity Threshold</Label>
          <span className="text-sm font-medium">{config.similarityThreshold.toFixed(2)}</span>
        </div>
        <Slider
          value={[config.similarityThreshold]}
          onValueChange={([value]) => onConfigChange({ ...config, similarityThreshold: value })}
          min={0}
          max={1}
          step={0.05}
        />
        <p className="text-xs text-muted-foreground">
          Minimum similarity score for retrieved chunks
        </p>
      </div>

      {/* Keywords Similarity Weight */}
      <div className="space-y-3">
        <div className="flex justify-between items-center">
          <Label>Keywords Similarity Weight</Label>
          <span className="text-sm font-medium">{config.keywordsSimilarityWeight.toFixed(2)}</span>
        </div>
        <Slider
          value={[config.keywordsSimilarityWeight]}
          onValueChange={([value]) => onConfigChange({ ...config, keywordsSimilarityWeight: value })}
          min={0}
          max={1}
          step={0.05}
        />
        <p className="text-xs text-muted-foreground">
          Weight of keyword matching vs vector similarity
        </p>
      </div>

      {/* Top N Chunks */}
      <div className="space-y-2">
        <Label htmlFor="topN">Top N Chunks</Label>
        <Input
          id="topN"
          type="number"
          min={1}
          max={20}
          value={config.topN}
          onChange={(e) => {
            const value = parseInt(e.target.value, 10)
            if (!isNaN(value) && value >= 1 && value <= 20) {
              onConfigChange({ ...config, topN: value })
            }
          }}
          className="w-24"
        />
        <p className="text-xs text-muted-foreground">
          Number of most relevant chunks to retrieve (1-20)
        </p>
      </div>

      {/* Show Quote */}
      <div className="flex items-center justify-between">
        <div className="space-y-0.5">
          <Label htmlFor="showQuote">Show Quote</Label>
          <p className="text-xs text-muted-foreground">
            Display source references in responses
          </p>
        </div>
        <Switch
          id="showQuote"
          checked={config.showQuote}
          onCheckedChange={(checked) => onConfigChange({ ...config, showQuote: checked })}
        />
      </div>

      {/* Reset Button */}
      <div className="pt-4">
        <Button type="button" variant="outline" onClick={handleReset}>
          Reset to Defaults
        </Button>
      </div>
    </div>
  )
}
